import { cn } from './cn.js';

import type {
  CSSProperties,
  HTMLAttributes,
  ReactElement,
  ReactNode,
  TdHTMLAttributes,
  ThHTMLAttributes,
} from 'react';

const tableWrapperStyle: CSSProperties = {
  background: 'var(--glass-bg, rgba(255,255,255,0.65))',
  backdropFilter: 'blur(var(--glass-blur-medium, 16px))',
  WebkitBackdropFilter: 'blur(var(--glass-blur-medium, 16px))',
  border: '1px solid var(--glass-border, rgba(255,255,255,0.60))',
  boxShadow: 'var(--glass-shadow, 0 4px 24px rgba(0,0,0,0.06))',
  borderRadius: 'var(--radius-xl, 12px)',
  overflow: 'hidden',
};

/* ── Table Root ── */
interface GlassTableProps extends HTMLAttributes<HTMLTableElement> {
  children: ReactNode;
}

export function GlassTable({ className, children, ...props }: GlassTableProps): ReactElement {
  return (
    <div style={tableWrapperStyle}>
      <div className="overflow-x-auto" style={{ scrollbarWidth: 'thin' }}>
        <table className={cn('w-full text-sm', className)} {...props}>
          {children}
        </table>
      </div>
    </div>
  );
}

/* ── Table Head ── */
interface GlassTableHeadProps extends HTMLAttributes<HTMLTableSectionElement> {
  children: ReactNode;
}

export function GlassTableHead({
  className,
  children,
  ...props
}: GlassTableHeadProps): ReactElement {
  return (
    <thead
      className={className}
      style={{
        background: 'var(--glass-row-header, rgba(255,255,255,0.70))',
        backdropFilter: 'blur(4px)',
      }}
      {...props}
    >
      {children}
    </thead>
  );
}

/* ── Table Body ── */
interface GlassTableBodyProps extends HTMLAttributes<HTMLTableSectionElement> {
  children: ReactNode;
}

export function GlassTableBody({
  className,
  children,
  ...props
}: GlassTableBodyProps): ReactElement {
  return (
    <tbody className={className} {...props}>
      {children}
    </tbody>
  );
}

/* ── Table Row ── */
interface GlassTableRowProps extends HTMLAttributes<HTMLTableRowElement> {
  children: ReactNode;
}

export function GlassTableRow({
  className,
  children,
  style,
  ...props
}: GlassTableRowProps): ReactElement {
  return (
    <tr
      className={cn(
        'transition-colors duration-150 ease-in-out',
        'hover:bg-[var(--glass-row-hover)]',
        className,
      )}
      style={{
        borderBottom: '1px solid var(--glass-border-subtle, rgba(255,255,255,0.35))',
        ...style,
      }}
      {...props}
    >
      {children}
    </tr>
  );
}

/* ── Table Header Cell ── */
interface GlassTableHeaderCellProps extends ThHTMLAttributes<HTMLTableCellElement> {
  children?: ReactNode;
}

export function GlassTableHeaderCell({
  className,
  children,
  ...props
}: GlassTableHeaderCellProps): ReactElement {
  return (
    <th
      className={cn(
        'px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider',
        className,
      )}
      {...props}
    >
      {children}
    </th>
  );
}

/* ── Table Cell ── */
interface GlassTableCellProps extends TdHTMLAttributes<HTMLTableCellElement> {
  children: ReactNode;
}

export function GlassTableCell({
  className,
  children,
  ...props
}: GlassTableCellProps): ReactElement {
  return (
    <td className={cn('px-4 py-3 text-text', className)} {...props}>
      {children}
    </td>
  );
}
